import React, { useCallback, useEffect, useRef, useState } from 'react'
import useLatest from 'react-use/lib/useLatest'
import useMeasure from 'react-use/lib/useMeasure'
import { mergeRefs } from 'react-merge-refs'
import { Gif } from '@giphy/react-components'
import type { IGif } from '@giphy/js-types'
import {
    arrow,
    flip,
    offset,
    shift,
    Side,
    useDismiss,
    useFloating,
    useInteractions,
} from '@floating-ui/react-dom-interactions'

import type { GifPopoverContext } from './types'

export function useGifPopover(): GifPopoverContext {
    const arrowRef = useRef<HTMLDivElement | null>(null)
    const [gif, setGif] = useState<IGif | null>(null)
    const [open, setOpen] = useState(false)

    const floatingBag = useFloating({
        middleware: [offset(8), shift({ padding: 4 }), flip(), arrow({ element: arrowRef, padding: 4 })],
        onOpenChange: setOpen,
        open,
        placement: 'top',
    })

    const floatingBagRef = useLatest(floatingBag)
    const [floatingMeasureRef, floatingRect] = useMeasure()
    const floating = mergeRefs([floatingBag.floating, floatingMeasureRef])
    const side = floatingBag.placement.split('-')[0] as Side

    // Disabling dismiss interaction for a short time to end the animation and make the popover component fully visible.
    // This helps to avoid the popover component flickering on double-click.
    const [dismissInteractionEnabled, setDismissInteractionEnabled] = useState(false)
    useEffect(() => {
        if (!open) {
            setDismissInteractionEnabled(false)
            return
        }

        const tId = setTimeout(() => setDismissInteractionEnabled(true), 350)
        return () => {
            clearTimeout(tId)
        }
    }, [open])

    const floatingInteractions = useInteractions([
        useDismiss(floatingBag.context, {
            ancestorScroll: true,
            enabled: dismissInteractionEnabled,
            outsidePressEvent: 'click',
        }),
    ])

    const onGifClick = useCallback(
        (gif: IGif, e: React.SyntheticEvent<HTMLElement, Event>) => {
            const { refs, update } = floatingBagRef.current
            if (e.currentTarget) {
                refs.reference.current = e.currentTarget.closest(Gif.className) ?? e.currentTarget
                update()
                setGif(gif)
                setOpen(true)
            } else {
                setOpen(false)
                setGif(null)
            }
        },
        [floatingBagRef],
    )

    return {
        ...floatingBag.context,
        arrowRef,
        floating,
        floatingRect,
        gif,
        interactions: floatingInteractions,
        onGifClick,
        side,
    }
}
